import { Component, inject, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { DTypeForm, DTypeFormTriggerField } from "@grupo-constant/next-dtype/form";
import { TriggerEvent, TriggerResponsible, TriggerShortcutExpr, TRIGGER_COLUMN_URI } from "@grupo-constant/next-dtype/types";
import { TranslocoModule, TranslocoService } from "@ngneat/transloco";
import * as lodash from 'lodash-es';
import { DialogsService } from "../dialogs.service";
import { DTypeModule } from "../../core/dtype/dtype.module";
import { NextServicio, PresenceConcept } from "../../modules/admin/services/services.types";
import { FuseConfirmationService } from "../../../@fuse/services/confirmation/confirmation.service";
import { FuseConfirmationDialogComponent } from "../../../@fuse/services/confirmation/dialog/dialog.component";

@Component({
    selector: 'app-presence-concept-dialog',
    templateUrl: './presence-concept-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
        .presence-concept-dialog-panel {

            .mat-mdc-dialog-container {
        
                .mat-mdc-dialog-surface {
                    padding: 0 !important;
                }
            }
        }
        `
    ],
    imports: [DTypeModule, FormsModule, MatButtonModule, MatIconModule, ReactiveFormsModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class PresenceConceptDialogComponent implements OnInit {

    dTypeForm: DTypeForm;

    public data: {
        dTypeForm: DTypeForm,
        namesInUse: string[],
        service: NextServicio
    } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<PresenceConceptDialogComponent>);
    private dialogsService = inject(DialogsService);
    private _fuseConfirmationService = inject(FuseConfirmationService);
    private transloco = inject(TranslocoService);

    ngOnInit() {
        this.dTypeForm = lodash.cloneDeep(this.data.dTypeForm);
    }

    closeDialog(concept?: PresenceConcept) {
        this.dialogRef.close(concept);
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.presenceconfig.saveConceptConfirm'));
        confirmDialog.afterClosed().subscribe(async (confirm) => {
            if (confirm === 'confirmed') {
                const form = this.dTypeForm.control;
                form.markAllAsTouched();
                if (form.valid) {
                    for (const field of Object.values(this.dTypeForm.fields)
                        .filter(f => f.type === TRIGGER_COLUMN_URI)) {
                        const needsToBeFilled = this.checkTriggerField({
                            value: form.get(field.name).value,
                            triggerField: (field as DTypeFormTriggerField),
                            event: TriggerEvent.ON_CREATE
                        });

                        if (needsToBeFilled) {
                            form.get(field.name)
                                .setValue(await this.getTriggerFieldValue((field as DTypeFormTriggerField)));
                        }
                    }
                    const concept: PresenceConcept = form.getRawValue();
                    if (!this.data.namesInUse.includes(concept.name)) {
                        this.closeDialog(concept);
                    } else {
                        this.dialogsService.openErrorDialog({
                            message: this.transloco.translate('services.presenceconfig.nameInUse')
                        });
                    }
                } else {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('errorsInForm')
                    });
                }
            }
        });
    }

    private openConfirmDialog(message: string): MatDialogRef<FuseConfirmationDialogComponent, any> {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

    private checkTriggerField(params: {
        value?: any;
        triggerField: DTypeFormTriggerField;
        event: TriggerEvent | TriggerEvent[];
    }): boolean {
        let needsToBeFilled = false;
        if (((params?.value === null || params?.value === undefined) &&
            params?.triggerField?.responsible === TriggerResponsible.FIRST) ||
            params?.triggerField?.responsible === TriggerResponsible.CLIENT) {
            if (typeof params.event === 'string' && params.triggerField.event === params.event) {
                needsToBeFilled = (params?.value === null || params?.value === undefined) ||
                    params.event === TriggerEvent.ON_OPEN_RESET || params.event === TriggerEvent.ON_QUEUE_RESET;
            } else if (Array.isArray(params.event)) {
                for (const event of params.event) {
                    if (params.triggerField.event === event) {
                        if ((params?.value === null || params?.value === undefined) ||
                            event === TriggerEvent.ON_OPEN_RESET || event === TriggerEvent.ON_QUEUE_RESET) {
                            needsToBeFilled = true;
                            break;
                        }
                    }
                }
            }
        }

        return needsToBeFilled;
    }

    async getTriggerFieldValue(triggerField: DTypeFormTriggerField) {
        let value: any = null;
        switch (triggerField.shortcutExpr) {
            case TriggerShortcutExpr.serviceId:
                value = this.data.service.serviceId;
                break;
            default:
                break;
        }
        return value;
    }
}